import styles from './CustomStaticPeriodDatePicker.module.scss';

import { LocalizationProvider } from '@mui/x-date-pickers1/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers1/AdapterDayjs';
import React from 'react';
import { StaticDatePicker } from '@mui/x-date-pickers1/StaticDatePicker';

import { PickersDay } from '@mui/x-date-pickers1';
import GlobalStyles from '@mui/material/GlobalStyles';
import { createTheme, styled, ThemeProvider } from '@mui/material/styles';
import isBetweenPlugin from 'dayjs/plugin/isBetween';
import dayjs from 'dayjs';
import 'dayjs/locale/it';

dayjs.extend(isBetweenPlugin);

const statsPickerGlobalStyles = (
  <GlobalStyles
    styles={{
      '[data-variant="stats"] .MuiCalendarOrClockPicker-root > div': {
        maxHeight: 'none !important',
        height: 'auto !important',
        width: '100% !important',
        overflow: 'visible !important',
      },
      '[data-variant="stats"] .MuiPickerStaticWrapper-content': {
        overflow: 'visible !important',
        backgroundColor: 'transparent !important',
        minWidth: '0 !important',
      },
      '[data-variant="stats"] .MuiCalendarPicker-root': {
        maxHeight: 'none !important',
        height: 'auto !important',
        overflow: 'visible !important',
      },
      '[data-variant="stats"] .PrivatePickersSlideTransition-root > *, [data-variant="stats"] .MuiDayPicker-slideTransition > *':
        {
          position: 'relative !important',
          top: 'auto !important',
          right: 'auto !important',
          left: 'auto !important',
          transform: 'none !important',
        },
      '[data-variant="stats"] .PrivatePickersSlideTransition-root, [data-variant="stats"] .MuiDayPicker-slideTransition':
        {
          height: 'auto !important',
          minHeight: '0 !important',
          overflow: 'visible !important',
        },
    }}
  />
);

const CustomPickersDay = styled(PickersDay, {
  shouldForwardProp: (prop) =>
    prop !== 'dayIsBetween' && prop !== 'isFirstDay' && prop !== 'isLastDay' && prop !== 'statsVariant',
})(({ dayIsBetween, isFirstDay, isLastDay, statsVariant }) => {
  const circleBase = statsVariant
    ? {
        borderRadius: '50% !important',
        margin: '2px',
        minWidth: '36px',
        minHeight: '36px',
      }
    : {};

  return {
    ...circleBase,
    ...(dayIsBetween &&
      (statsVariant
        ? {
            background: '#E4E7FF !important',
            color: '#6984FF !important',
            '&:hover, &:focus': {
              background: '#D4D8FF !important',
              color: '#6984FF !important',
            },
          }
        : {
            background: 'linear-gradient(180deg, #E1E7FF 0%, #F2EBFF 100%) !important',
            color: '#6984FF',
            '&:hover, &:focus': {
              background: '#858AF9',
              color: 'white',
            },
          })),
    ...((isFirstDay || isLastDay) && {
      background: '#858AF9 !important',
      color: 'white !important',
      '&:focus.Mui-selected, &.Mui-selected, &:hover, &:focus': {
        background: '#858AF9 !important',
        color: 'white !important',
      },
    }),
  };
});

const buildTheme = (statsVariant) =>
  createTheme({
    components: {
      MuiDayPicker: {
        styleOverrides: {
          header: {
            justifyContent: 'space-around',
          },
          weekDayLabel: {
            margin: statsVariant ? '0 2px' : undefined,
          },
          slideTransition: statsVariant
            ? {
                // MUI default is (36 + 4) * 6 = 240; keep in sync with day margin below
                minHeight: (36 + 2 * 2) * 6,
              }
            : undefined,
          weekContainer: statsVariant
            ? {
                margin: '2px 0',
              }
            : undefined,
        },
      },
      MuiPickersCalendarHeader: {
        styleOverrides: {
          root: {
            position: 'relative',
            margin: statsVariant ? '0 0 8px' : 'auto',
            padding: statsVariant ? '0 4px' : 0,
            maxWidth: '100%',
          },
          labelContainer: {
            width: statsVariant ? 'auto' : '200px',
            margin: '0 auto',
            display: 'flex',
            justifyContent: 'center',
          },
          label: {
            fontSize: statsVariant ? '15px' : '17px',
            fontWeight: statsVariant ? 600 : 400,
            '&:first-letter': {
              textTransform: 'capitalize',
            },
          },
        },
      },
      MuiPickersArrowSwitcher: {
        styleOverrides: {
          root: statsVariant
            ? {
                position: 'absolute',
                left: 0,
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                pointerEvents: 'none',
              }
            : {
                left: '-50px',
                position: 'absolute',
              },
          spacer: statsVariant
            ? {
                display: 'none',
              }
            : {
                width: '250px',
              },
          button: statsVariant
            ? {
                pointerEvents: 'auto',
                background: 'transparent',
                color: '#AEAEB2',
                '&:hover': {
                  background: '#F5F5F5',
                },
                '&.Mui-disabled': {
                  opacity: 0.35,
                },
              }
            : undefined,
        },
      },
      MuiPickersDay: {
        styleOverrides: {
          root: {
            fontWeight: statsVariant ? 500 : 400,
            fontSize: statsVariant ? '14px' : '17px',
            lineHeight: '20px',
            color: statsVariant ? '#1F1F1F' : '#787878',
            background: statsVariant ? 'transparent' : '#F5F5F5',
            minWidth: '36px',
            minHeight: '36px',
            // Keep 2px margin: MUI week grid height assumes (36 + 4) * 6
            margin: statsVariant ? '2px' : '4px',
            borderRadius: statsVariant ? '50%' : undefined,
            '&.Mui-disabled': {
              background: 'transparent !important',
              color: statsVariant ? '#D0D0D0 !important' : undefined,
              opacity: statsVariant ? 1 : undefined,
              cursor: statsVariant ? 'not-allowed' : undefined,
              pointerEvents: statsVariant ? 'none' : undefined,
            },
            '&:focus.Mui-selected': {
              background: statsVariant ? undefined : 'transparent',
              color: statsVariant ? undefined : '#787878',
            },
            '&.Mui-selected': {
              background: statsVariant ? undefined : 'transparent',
              color: statsVariant ? undefined : '#787878',
            },
            '&:hover': {
              background: statsVariant ? '#EEF0FF' : '#e2e2e2',
              color: statsVariant ? '#1F1F1F' : undefined,
            },
            '&.MuiPickersDay-today': statsVariant
              ? {
                  border: '1px solid #858AF9',
                  background: 'transparent',
                  fontWeight: 700,
                }
              : undefined,
          },
          dayOutsideMonth: {
            color: '#C8C8C8',
            opacity: statsVariant ? 0.35 : undefined,
            pointerEvents: statsVariant ? 'none' : undefined,
            cursor: statsVariant ? 'default' : undefined,
          },
        },
      },
      MuiCalendarPicker: {
        styleOverrides: {
          root: statsVariant
            ? {
                width: '100%',
                maxWidth: '320px',
                maxHeight: 'none',
                height: 'auto',
                margin: '0 auto',
                overflow: 'visible',
              }
            : undefined,
        },
      },
      MuiPickerStaticWrapper: {
        styleOverrides: {
          root: statsVariant
            ? {
                minWidth: 0,
              }
            : undefined,
          content: statsVariant
            ? {
                minWidth: 0,
                overflow: 'visible',
                backgroundColor: 'transparent',
              }
            : undefined,
        },
      },
    },
  });

const STATS_MUI_THEME = buildTheme(true);
const DEFAULT_MUI_THEME = buildTheme(false);

const CustomStaticPeriodDatePicker = ({
  period,
  setPeriod,
  error,
  touched,
  disabledDates = [],
  disablePast = true,
  disableFuture = false,
  variant = 'default',
}) => {
  const statsVariant = variant === 'stats';
  const muiTheme = statsVariant ? STATS_MUI_THEME : DEFAULT_MUI_THEME;
  const pickingStartRef = React.useRef(true);
  const rangeStartRef = React.useRef(null);

  React.useEffect(() => {
    if (!statsVariant) return;
    const hasStart = period?.start?.isValid?.();
    const hasEnd = period?.end?.isValid?.();
    if (!hasStart && !hasEnd) {
      pickingStartRef.current = true;
      rangeStartRef.current = null;
    } else if (hasStart && !hasEnd) {
      pickingStartRef.current = false;
      rangeStartRef.current = period.start.startOf('day');
    } else if (hasStart && hasEnd) {
      // Range completo: il prossimo tap riparte da capo
      pickingStartRef.current = true;
      rangeStartRef.current = null;
    }
  }, [statsVariant, period?.start, period?.end]);

  const renderWeekPickerDay = React.useCallback(
    (date, _selectedDates, pickersDayProps) => {
      const { start, end } = period || {};

      const isFirstDay = start && date.isSame(start, 'day');
      const isLastDay = end && date.isSame(end, 'day');
      const dayIsBetween = start && end && date.isBetween(start, end, null, '[]');

      return (
        <CustomPickersDay
          {...pickersDayProps}
          disableMargin
          statsVariant={statsVariant}
          dayIsBetween={dayIsBetween}
          isFirstDay={isFirstDay}
          isLastDay={isLastDay}
        />
      );
    },
    [period?.start, period?.end, statsVariant],
  );

  const periodCounter = React.useCallback(
    (date) => {
      const normalized = dayjs(date).startOf('day');

      if (statsVariant) {
        // Usa ref: evita di perdere lo start per closure stale (bug "non prende la fine")
        if (pickingStartRef.current || !rangeStartRef.current?.isValid?.()) {
          pickingStartRef.current = false;
          rangeStartRef.current = normalized;
          return { start: normalized, end: null };
        }

        const start = rangeStartRef.current;
        pickingStartRef.current = true;
        rangeStartRef.current = null;

        if (normalized.isBefore(start, 'day')) {
          return { start: normalized, end: start };
        }
        // Stesso giorno incluso (es. 01/07 – 01/07)
        return { start, end: normalized };
      }

      const { start, end } = period || {};
      const isSameDay = start && end && start.isSame(end, 'day');
      const startDayDiff = start && start.diff(normalized, 'day');
      const endDayDiff = end && end.diff(normalized, 'day');
      return {
        start: !start
          ? normalized
          : endDayDiff === 0 && startDayDiff === 0
            ? null
            : endDayDiff === 0
              ? end
              : (startDayDiff > 0 && isSameDay) ||
                  (Math.abs(startDayDiff) <= Math.abs(endDayDiff) && !isSameDay)
                ? normalized
                : start,
        end: !end
          ? normalized
          : endDayDiff === 0 && startDayDiff === 0
            ? null
            : startDayDiff === 0
              ? start
              : (endDayDiff < 0 && isSameDay) ||
                  (Math.abs(endDayDiff) < Math.abs(startDayDiff) && !isSameDay)
                ? normalized
                : end,
      };
    },
    [period, statsVariant],
  );

  const handleChange = React.useCallback(
    (date) => {
      if (!date) return;
      setPeriod(periodCounter(date));
    },
    [periodCounter, setPeriod],
  );

  const shouldDisableDate = React.useCallback(
    (date) => Boolean(disabledDates.find((disableDate) => date.isSame(disableDate, 'day'))),
    [disabledDates],
  );

  const dayOfWeekFormatter = React.useCallback(
    (day) => <div className={styles.day_name_value}>{day}</div>,
    [],
  );

  // In attesa della data fine: value=null così MUI rifa onChange anche sullo stesso giorno di inizio
  const pickerValue =
    statsVariant && period?.start?.isValid?.() && !period?.end?.isValid?.()
      ? null
      : period?.start || null;

  return (
    <div className={styles.picker_wrapper} data-variant={variant}>
      {statsVariant ? statsPickerGlobalStyles : null}
      {touched && error && <div className={styles.error_message}>{error}</div>}
      <ThemeProvider theme={muiTheme}>
        <LocalizationProvider
          dateAdapter={AdapterDayjs}
          adapterLocale={statsVariant ? 'it' : undefined}
        >
          <StaticDatePicker
            openTo="day"
            displayStaticWrapperAs="desktop"
            reduceAnimations={statsVariant}
            onChange={handleChange}
            disablePast={disablePast}
            disableFuture={disableFuture}
            maxDate={disableFuture ? dayjs() : undefined}
            views={['day']}
            renderDay={renderWeekPickerDay}
            renderInput={() => null}
            value={pickerValue}
            componentsProps={{
              actionBar: { actions: [] },
            }}
            dayOfWeekFormatter={dayOfWeekFormatter}
            shouldDisableDate={shouldDisableDate}
            showDaysOutsideCurrentMonth={!statsVariant}
          />
        </LocalizationProvider>
      </ThemeProvider>
    </div>
  );
};

export default React.memo(CustomStaticPeriodDatePicker);
